import React, { useEffect, useState, useMemo } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme, Switch, FormControlLabel, InputLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
// 1. استيراد مكتبة التوست
import toast from "react-hot-toast";

import { useCreateBrand } from "../../hooks/brands/useCreateBrand";
import { useUpdateBrand } from "../../hooks/brands/useUpdateBrand";
import { useGetCategoriesForSelect } from "../../hooks/categories/useGetCategoriesForSelect";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";
import { buildHierarchicalOptions } from "../../utils/helpers";

const FormAddEditBrands = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [slug, setSlug] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [logo, setLogo] = useState(null); // Can be: image object {id, url}, temp filename (string), or null

    // Hooks
    const { createBrandMutate, isCreating } = useCreateBrand();
    const { editBrand, isUpdate } = useUpdateBrand();
    const { options: categoryOptions, isLoading: isLoadingCats } = useGetCategoriesForSelect();

    // Hierarchy processing for categories selection
    const hierarchicalCategories = useMemo(() => {
        return buildHierarchicalOptions(categoryOptions);
    }, [categoryOptions]);

    const loadingSubmit = isCreating || isUpdate;

    // Auto-slug generation from English name
    useEffect(() => {
        if (nameEn) {
            setSlug(slugify(nameEn));
        }
    }, [nameEn]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            // Handle if data is wrapped in an array [ {...} ]
            const data = Array.isArray(currentData) ? currentData[0] : currentData;

            if (data) {
                setNameAr(data?.name?.ar || "");
                setNameEn(data?.name?.en || "");
                setSlug(data.slug || "");
                setIsActive(data.is_active ?? true);

                // Use category_ids as per the provided API response structure
                const catIds = data.category_ids || data.categories?.map(c => c.id) || [];
                setSelectedCategories(catIds.map(id => String(id)));

                // Set logo state for InputFileUpload
                // This can be the logo object or a URL string, InputFileUpload handles both
                setLogo(data.logo || data.logo_url || null);
            }
        }
    }, [isEdit, currentData]);

    const handleLogoChange = (e) => {
        // Now e.target.value contains the filename (string) or null
        const val = e.target.value;
        setLogo(val);
    };

    const handleCategoryChange = (e) => {
        setSelectedCategories(e.target.value);
    };

    const SubmitForm = (e) => {
        e.preventDefault();

        // ============================================================
        // 2. التحقق من اختيار الفئات (Categories Validation)
        // ============================================================
        if (!selectedCategories || selectedCategories.length === 0) {
            const errorMsg = isArabic
                ? "يرجى اختيار فئة واحدة على الأقل لإتمام العملية"
                : "Please select at least one category to proceed";

            // إظهار التوست
            toast.error(errorMsg);

            // إيقاف تنفيذ الدالة
            return;
        }
        // ============================================================

        // ✅ Construct FormData here to keep API Slim (Standard Pattern)
        const formData = new FormData();

        if (isEdit) formData.append('_method', 'PUT');

        formData.append('name[en]', nameEn ? nameEn : nameAr);
        formData.append('name[ar]', nameAr ? nameAr : nameEn);
        formData.append('slug', slug);
        formData.append('is_active', isActive ? '1' : '0');

        if (logo && typeof logo === 'string') {
            // It's a temp filename from the auto-upload logic
            formData.append('logo', logo);
        } else if (logo === null) {
            // Explicitly deleted
            formData.append('remove_logo', '1');
        }
        // If logo is an object, it's an existing image that wasn't changed, 
        // no need to append it.

        if (selectedCategories && selectedCategories.length > 0) {
            selectedCategories.forEach(id => {
                formData.append('category_ids[]', id);
            });
        }

        if (isEdit && currentData?.id) {
            editBrand(
                { id: currentData.id, brandData: formData },
                { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
            );
        } else {
            createBrandMutate(formData, {
                onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); }
            });
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل علامة تجارية" : "Edit Brand") : (isArabic ? "إضافة علامة تجارية جديدة" : "Create New Brand")}
                    </Typography>

                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Logo Upload */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2, }}>
                        <FormInput
                            type="file"
                            name="logo"
                            labelImage={isArabic ? "شعار العلامة" : "Brand Logo"}
                            value={logo}
                            onChange={handleLogoChange}
                            width={120}
                            height={120}
                            required={!isEdit && !logo}
                        />
                    </Grid>

                    {/* Multilingual Names */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="الاسم (عربي)"
                                placeholder="أدخل اسم العلامة التجارية بالعربي"
                                type="text"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Name (English)"
                                placeholder="Enter brand name in English"
                                type="text"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug)" : "Slug"}
                            placeholder="apple-brand"
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            disabled={true}
                            required
                        />
                    </Grid>

                    {/* Associated Categories (Multi-Select) */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الفئات المرتبطة" : "Associated Categories"}
                            type="multi_select"
                            name="category_ids"
                            selectedValue={selectedCategories}
                            handleSelectChange={handleCategoryChange}
                            options={hierarchicalCategories}
                            disabled={isLoadingCats}
                            required={true} // Visual asterisk
                        />
                    </Grid>

                    {/* Is Active Switch */}
                    <Grid item xs={12} md={6}>
                        <InputLabel
                            sx={{
                                my: 1,
                                fontWeight: 'bold',
                                textAlign: isArabic ? 'right' : 'left',
                                direction: isArabic ? 'rtl' : 'ltr',
                            }}
                        >
                            {isArabic ? "حالة العلامة التجارية" : "Brand Status"}
                        </InputLabel>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            border: '1px solid',
                            borderColor: theme => theme.palette.divider,
                            borderRadius: '8px',
                            minHeight: '56px', // Match standard TextField height
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: theme => theme.palette.primary.main,
                            }
                        }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: theme => theme.palette.text.secondary }}>
                                {isActive ? (isArabic ? "تحويل إلى غير نشط" : "Active Now") : (isArabic ? "تحويل إلى نشط" : "Inactive Now")}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                                sx={{ mr: 0, ml: 0 }}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditBrands;